import { MAX_TEXT_LENGTH } from "./constants.js";
import { trimmedStringFromUnknown } from "./shared.js";
import { openMeldRuntimeProfileIdSchema } from "./profile.js";
import { navigationActionSchema } from "../../navigation-action.js";
import * as z$1 from "zod/v4";
//#region src/schemas/protocol/message-envelope.ts
const MULTI_SPACE_PATTERN = /\s+/g;
const CANONICAL_MENTION_LITERAL_PATTERN = /^@(?<name>.+)\((?<type>wake|reference|mention)\)$/u;
const RELATION_TEXT_PATTERN = /\((wake|reference|mention|cc)\)/gu;
const WORD_CHARACTER_PATTERN = /\w/u;
const SPACE_MESSAGE_ADDRESSING_RELATION_VALUES = [
	"wake",
	"reference",
	"mention"
];
const MESSAGE_MENTION_TYPE_VALUES = SPACE_MESSAGE_ADDRESSING_RELATION_VALUES;
const MESSAGE_ENVELOPE_TARGET_FIELD_VALUES = [
	"activationTargets",
	"referenceTargets",
	"mentionTargets"
];
const SPACE_MESSAGE_ADDRESSING_TARGET_FIELD_VALUES = MESSAGE_ENVELOPE_TARGET_FIELD_VALUES;
const SPACE_MESSAGE_ADDRESSING_TARGET_KIND_VALUES = ["human_profile", "agent_profile"];
const spaceMessageAddressingRelationSchema = z$1.enum(SPACE_MESSAGE_ADDRESSING_RELATION_VALUES);
const messageMentionTypeSchema = spaceMessageAddressingRelationSchema;
const messageEnvelopeTargetFieldSchema = z$1.enum(MESSAGE_ENVELOPE_TARGET_FIELD_VALUES);
const spaceMessageAddressingTargetKindSchema = z$1.enum(SPACE_MESSAGE_ADDRESSING_TARGET_KIND_VALUES);
const MESSAGE_ENVELOPE_TARGET_FIELD_BY_MENTION_TYPE = {
	wake: "activationTargets",
	reference: "referenceTargets",
	mention: "mentionTargets"
};
const SPACE_MESSAGE_ADDRESSING_TARGET_KIND_SUPPORT_BY_RELATION = {
	wake: ["agent_profile"],
	reference: ["agent_profile"],
	mention: ["human_profile"]
};
const normalizeCanonicalMentionName = (value) => {
	const normalized = trimmedStringFromUnknown(value).replace(MULTI_SPACE_PATTERN, " ").trim();
	if (normalized.length === 0) return;
	return normalized;
};
const canonicalMentionNameSchema = z$1.preprocess(normalizeCanonicalMentionName, z$1.string().min(1).refine((value) => !(value.includes("(") || value.includes(")")), "Canonical mention name must not contain parentheses"));
const canonicalMentionLiteralSchema = z$1.strictObject({
	name: canonicalMentionNameSchema,
	type: messageMentionTypeSchema
});
const canonicalMentionTextFragmentSchema = z$1.strictObject({
	start: z$1.number().int().min(0),
	end: z$1.number().int().min(1),
	text: z$1.string().min(1),
	parsedLiteral: canonicalMentionLiteralSchema.nullable()
});
const buildTargetListSchema = (fieldName) => z$1.array(openMeldRuntimeProfileIdSchema).superRefine((targets, context) => {
	const seenTargets = /* @__PURE__ */ new Set();
	for (const [index, targetProfileId] of targets.entries()) {
		if (seenTargets.has(targetProfileId)) {
			context.addIssue({
				code: z$1.ZodIssueCode.custom,
				message: `${fieldName} must not contain duplicate profileId`,
				path: [index]
			});
			continue;
		}
		seenTargets.add(targetProfileId);
	}
});
const activationTargetsSchema = buildTargetListSchema("activationTargets");
const referenceTargetsSchema = buildTargetListSchema("referenceTargets");
const mentionTargetsSchema = buildTargetListSchema("mentionTargets");
const replyToSignalIdSchema = z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1));
const validateDistinctTargetLists = (input, context) => {
	const seenByTarget = /* @__PURE__ */ new Map();
	for (const fieldName of MESSAGE_ENVELOPE_TARGET_FIELD_VALUES) for (const targetProfileId of input[fieldName] ?? []) {
		const existingField = seenByTarget.get(targetProfileId);
		if (!existingField) {
			seenByTarget.set(targetProfileId, fieldName);
			continue;
		}
		context.addIssue({
			code: z$1.ZodIssueCode.custom,
			message: `${fieldName} must not reuse profileId from ${existingField}`,
			path: [fieldName]
		});
	}
};
const currentMessageEnvelopeSchema = z$1.strictObject({
	activationTargets: activationTargetsSchema,
	referenceTargets: referenceTargetsSchema,
	mentionTargets: mentionTargetsSchema.optional(),
	navigationAction: navigationActionSchema.optional(),
	replyToSignalId: replyToSignalIdSchema.optional(),
	text: z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(MAX_TEXT_LENGTH))
}).superRefine(validateDistinctTargetLists);
const currentMessageEnvelopeInputSchema = z$1.strictObject({
	activationTargets: activationTargetsSchema.optional(),
	referenceTargets: referenceTargetsSchema.optional(),
	mentionTargets: mentionTargetsSchema.optional(),
	navigationAction: navigationActionSchema.optional(),
	replyToSignalId: replyToSignalIdSchema.optional(),
	text: z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(MAX_TEXT_LENGTH))
}).superRefine((value, context) => validateDistinctTargetLists({
	activationTargets: value.activationTargets ?? [],
	referenceTargets: value.referenceTargets ?? [],
	mentionTargets: value.mentionTargets ?? []
}, context));
/**
* Time-bounded compatibility boundary for deployed clients and persisted
* signals authored before CC was removed. A valid legacy `ccTargets` list is
* folded into Reference before the value enters the current domain model.
*
* Removal condition: the minimum supported Web, CLI, Local Service, Gateway,
* Core, and Remote Agent versions all author and consume the current
* envelope, and production history has been durably migrated or retained
* behind a dedicated historical decoder.
*/
const normalizeLegacyCcTargetsAsReference = (value) => {
	if (!(value && typeof value === "object") || Array.isArray(value)) return value;
	const record = value;
	if (!Object.hasOwn(record, "ccTargets")) return value;
	const legacyCcTargets = record.ccTargets;
	const referenceTargets = record.referenceTargets;
	if (!Array.isArray(legacyCcTargets) || referenceTargets !== void 0 && !Array.isArray(referenceTargets)) return value;
	const { ccTargets: _legacyCcTargets, ...currentFields } = record;
	return {
		...currentFields,
		referenceTargets: [...Array.isArray(referenceTargets) ? referenceTargets : [], ...legacyCcTargets]
	};
};
const messageEnvelopeSchema = z$1.preprocess(normalizeLegacyCcTargetsAsReference, currentMessageEnvelopeSchema);
const messageEnvelopeInputSchema = z$1.preprocess(normalizeLegacyCcTargetsAsReference, currentMessageEnvelopeInputSchema);
const normalizeMessageEnvelope = (value) => {
	const parsed = messageEnvelopeInputSchema.safeParse(value);
	if (!parsed.success) throw parsed.error;
	const normalized = {
		activationTargets: parsed.data.activationTargets ?? [],
		referenceTargets: parsed.data.referenceTargets ?? [],
		mentionTargets: parsed.data.mentionTargets ?? [],
		...parsed.data.navigationAction ? { navigationAction: parsed.data.navigationAction } : {},
		text: parsed.data.text,
		...parsed.data.replyToSignalId ? { replyToSignalId: parsed.data.replyToSignalId } : {}
	};
	const canonical = messageEnvelopeSchema.safeParse(normalized);
	if (!canonical.success) throw canonical.error;
	return canonical.data;
};
const parseCanonicalMentionLiteral = (value) => {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	if (trimmed.length === 0) return null;
	const match = trimmed.match(CANONICAL_MENTION_LITERAL_PATTERN);
	if (!match?.groups) return null;
	const name = match.groups.name;
	const type = match.groups.type;
	if (typeof name !== "string" || typeof type !== "string") return null;
	if (name.trim() !== name) return null;
	const parsed = canonicalMentionLiteralSchema.safeParse({
		name,
		type
	});
	if (!parsed.success) return null;
	return parsed.data;
};
const formatCanonicalMentionLiteral = (value) => {
	const parsed = canonicalMentionLiteralSchema.safeParse(value);
	if (!parsed.success) throw parsed.error;
	return `@${parsed.data.name}(${parsed.data.type})`;
};
const collectCanonicalMentionTextFragments = (value) => {
	if (typeof value !== "string" || value.length === 0) return [];
	const fragments = [];
	let cursor = 0;
	while (cursor < value.length) {
		const atIndex = value.indexOf("@", cursor);
		if (atIndex < 0) break;
		if (shouldSkipCanonicalMentionAt(value, atIndex)) {
			cursor = atIndex + 1;
			continue;
		}
		const lineEndIndex = value.indexOf("\n", atIndex);
		const nextAtIndex = value.indexOf("@", atIndex + 1);
		const searchEnd = Math.min(lineEndIndex < 0 ? value.length : lineEndIndex, nextAtIndex < 0 ? value.length : nextAtIndex);
		const lineFragment = value.slice(atIndex, searchEnd);
		const relationEndOffset = findCanonicalMentionRelationEnd(lineFragment);
		if (relationEndOffset === null) {
			cursor = atIndex + 1;
			continue;
		}
		const rawMentionText = lineFragment.slice(0, relationEndOffset);
		const leadingTrimmedLength = rawMentionText.length - rawMentionText.trimStart().length;
		const mentionText = rawMentionText.trim();
		if (!mentionText) {
			cursor = atIndex + Math.max(1, relationEndOffset);
			continue;
		}
		const start = atIndex + leadingTrimmedLength;
		const end = start + mentionText.length;
		const fragment = canonicalMentionTextFragmentSchema.safeParse({
			start,
			end,
			text: mentionText,
			parsedLiteral: parseCanonicalMentionLiteral(mentionText)
		});
		if (!fragment.success) throw fragment.error;
		fragments.push(fragment.data);
		cursor = atIndex + relationEndOffset;
	}
	return fragments;
};
function shouldSkipCanonicalMentionAt(value, atIndex) {
	const previous = atIndex > 0 ? value[atIndex - 1] ?? "" : "";
	return previous === "\\" || WORD_CHARACTER_PATTERN.test(previous);
}
function findCanonicalMentionRelationEnd(value) {
	RELATION_TEXT_PATTERN.lastIndex = 0;
	const relationMatch = RELATION_TEXT_PATTERN.exec(value);
	if (relationMatch?.index !== void 0) return relationMatch.index + relationMatch[0].length;
	return null;
}
const resolveMessageEnvelopeTargetField = (value) => {
	const parsed = messageMentionTypeSchema.safeParse(value);
	if (!parsed.success) throw parsed.error;
	return MESSAGE_ENVELOPE_TARGET_FIELD_BY_MENTION_TYPE[parsed.data];
};
const resolveSpaceMessageAddressingTargetField = (value) => {
	const parsed = spaceMessageAddressingRelationSchema.safeParse(value);
	if (!parsed.success) throw parsed.error;
	return MESSAGE_ENVELOPE_TARGET_FIELD_BY_MENTION_TYPE[parsed.data];
};
const isSpaceMessageAddressingRelationSupportedForTargetKind = (input) => {
	const relation = spaceMessageAddressingRelationSchema.safeParse(input.relation);
	if (!relation.success) throw relation.error;
	const targetKind = spaceMessageAddressingTargetKindSchema.safeParse(input.targetKind);
	if (!targetKind.success) throw targetKind.error;
	return SPACE_MESSAGE_ADDRESSING_TARGET_KIND_SUPPORT_BY_RELATION[relation.data].includes(targetKind.data);
};
const getSpaceMessageAddressingRelationSupportForTargetKind = (value) => {
	const targetKind = spaceMessageAddressingTargetKindSchema.safeParse(value);
	if (!targetKind.success) throw targetKind.error;
	return {
		wake: isSpaceMessageAddressingRelationSupportedForTargetKind({
			relation: "wake",
			targetKind: targetKind.data
		}),
		reference: isSpaceMessageAddressingRelationSupportedForTargetKind({
			relation: "reference",
			targetKind: targetKind.data
		}),
		mention: isSpaceMessageAddressingRelationSupportedForTargetKind({
			relation: "mention",
			targetKind: targetKind.data
		})
	};
};
//#endregion
export { MESSAGE_ENVELOPE_TARGET_FIELD_VALUES, MESSAGE_MENTION_TYPE_VALUES, SPACE_MESSAGE_ADDRESSING_RELATION_VALUES, SPACE_MESSAGE_ADDRESSING_TARGET_FIELD_VALUES, SPACE_MESSAGE_ADDRESSING_TARGET_KIND_VALUES, canonicalMentionLiteralSchema, canonicalMentionTextFragmentSchema, collectCanonicalMentionTextFragments, formatCanonicalMentionLiteral, getSpaceMessageAddressingRelationSupportForTargetKind, isSpaceMessageAddressingRelationSupportedForTargetKind, messageEnvelopeInputSchema, messageEnvelopeSchema, messageEnvelopeTargetFieldSchema, messageMentionTypeSchema, normalizeMessageEnvelope, parseCanonicalMentionLiteral, resolveMessageEnvelopeTargetField, resolveSpaceMessageAddressingTargetField, spaceMessageAddressingRelationSchema, spaceMessageAddressingTargetKindSchema };

//# sourceMappingURL=message-envelope.js.map